import { useEffect, useMemo, useState } from 'react';
import { Landmark, Save, ShieldCheck, Users } from 'lucide-react';
import api from '../lib/api';
import { useAuthStore } from '../store/authStore';

type BankingEnvironment = 'SANDBOX' | 'PRODUCTION';

interface BankingEnvironmentConfig {
  enabled?: boolean;
  baseUrl?: string;
  clientId?: string;
  clientSecret?: string;
  timeoutMs?: number;
  useMtls?: boolean;
  hasMtlsCertificate?: boolean;
  hasMtlsKey?: boolean;
}

interface BankingConfigResponse {
  activeEnvironment: BankingEnvironment;
  sandbox: BankingEnvironmentConfig;
  production: BankingEnvironmentConfig;
}

interface AccessBlueprint {
  profiles?: Record<string, string>;
  criticalActions?: Record<string, string[]>;
}

interface UserRow {
  id: string;
  profile?: string;
}

const parseApiError = (error: any, fallback: string) =>
  error?.response?.data?.message || error?.response?.data?.details?.message || fallback;

export default function BankConfPage() {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState<BankingConfigResponse | null>(null);
  const [accessBlueprint, setAccessBlueprint] = useState<AccessBlueprint | null>(null);
  const [profileStats, setProfileStats] = useState<Record<string, number>>({});

  const canEditConfig = useMemo(() => ['ADMIN', 'DIRETOR'].includes(user?.profile || ''), [user?.profile]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [cfgRes, accessRes, usersRes] = await Promise.all([
        api.get('/banking/config'),
        api.get('/banking/access-blueprint'),
        api.get('/users?limit=200'),
      ]);

      setForm(cfgRes.data);
      setAccessBlueprint(accessRes.data);

      const users = (usersRes.data?.data || []) as UserRow[];
      const stats = users.reduce((acc, item) => {
        const profile = item.profile || 'SEM_PERFIL';
        acc[profile] = (acc[profile] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
      setProfileStats(stats);
    } catch (error: any) {
      alert(parseApiError(error, 'Falha ao carregar BankConf.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const saveConfig = async () => {
    if (!form) return;
    try {
      setSaving(true);
      await api.patch('/banking/config', {
        activeEnvironment: form.activeEnvironment,
        sandbox: form.sandbox,
        production: form.production,
      });
      await loadData();
      alert('Configuracao do BankConf salva com sucesso.');
    } catch (error: any) {
      alert(parseApiError(error, 'Falha ao salvar configuracao.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Carregando BankConf...</div>
      </div>
    );
  }

  const envs: BankingEnvironment[] = ['SANDBOX', 'PRODUCTION'];

  return (
    <div className="space-y-6">
      <div className="card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2"><Landmark className="w-6 h-6" /> BankConf</h1>
            <p className="text-gray-600 mt-1">Configuracao bancaria, perfis e niveis de acesso.</p>
          </div>
          <div className="text-sm rounded-lg px-3 py-2 bg-primary-50 text-primary-700">Perfil atual: <strong>{user?.profile || 'SEM PERFIL'}</strong></div>
        </div>
      </div>

      <div className="card space-y-4">
        <h2 className="text-lg font-semibold flex items-center gap-2"><Users className="w-5 h-5" /> Perfis e acessos</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          {Object.entries(profileStats).map(([profile, total]) => (
            <div key={profile} className="rounded-lg border p-3">
              <p className="font-semibold">{profile}</p>
              <p className="text-gray-600">{total} usuario(s)</p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          {Object.entries(accessBlueprint?.profiles || {}).map(([profile, rule]) => (
            <div key={profile} className="rounded-lg border p-3">
              <p className="font-semibold">{profile}</p>
              <p className="text-gray-600">{rule}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="card space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2"><ShieldCheck className="w-5 h-5" /> Configuracao de ambiente</h2>
          <button className="btn-primary flex items-center gap-2" onClick={saveConfig} disabled={!canEditConfig || saving}><Save className="w-4 h-4" /> {saving ? 'Salvando...' : 'Salvar'}</button>
        </div>

        <div>
          <label className="label">Ambiente ativo</label>
          <select
            className="input w-60"
            value={form.activeEnvironment}
            onChange={(e) => setForm((prev) => prev ? ({ ...prev, activeEnvironment: e.target.value as BankingEnvironment }) : prev)}
            disabled={!canEditConfig}
          >
            <option value="SANDBOX">Homologacao</option>
            <option value="PRODUCTION">Producao</option>
          </select>
        </div>

        {envs.map((env) => {
          const block = env === 'SANDBOX' ? form.sandbox : form.production;
          return (
            <div key={env} className="rounded-xl border p-4 space-y-3">
              <h3 className="font-semibold">{env === 'SANDBOX' ? 'Homologacao' : 'Producao'}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><label className="label">Base URL</label><input className="input" value={block.baseUrl || ''} onChange={(e) => setForm((prev) => prev ? ({ ...prev, [env === 'SANDBOX' ? 'sandbox' : 'production']: { ...block, baseUrl: e.target.value } }) : prev)} disabled={!canEditConfig} /></div>
                <div><label className="label">Client ID</label><input className="input" value={block.clientId || ''} onChange={(e) => setForm((prev) => prev ? ({ ...prev, [env === 'SANDBOX' ? 'sandbox' : 'production']: { ...block, clientId: e.target.value } }) : prev)} disabled={!canEditConfig} /></div>
                <div><label className="label">Client Secret</label><input className="input" value={block.clientSecret || ''} onChange={(e) => setForm((prev) => prev ? ({ ...prev, [env === 'SANDBOX' ? 'sandbox' : 'production']: { ...block, clientSecret: e.target.value } }) : prev)} disabled={!canEditConfig} /></div>
                <div><label className="label">Timeout (ms)</label><input className="input" type="number" value={block.timeoutMs || 30000} onChange={(e) => setForm((prev) => prev ? ({ ...prev, [env === 'SANDBOX' ? 'sandbox' : 'production']: { ...block, timeoutMs: Number(e.target.value) } }) : prev)} disabled={!canEditConfig} /></div>
              </div>
              <p className="text-xs text-gray-600">mTLS certificado: {block.hasMtlsCertificate ? 'Sim' : 'Nao'} | chave: {block.hasMtlsKey ? 'Sim' : 'Nao'}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
